import React, { memo } from "react";
import { Statistic, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import CountUp from "react-countup";
import { formatCompactCurrency, formatCompactCount } from "../utils";
import { MetricSkeleton } from "./DashboardSkeleton";

const countFormatter = (value) => <CountUp end={value} separator="," />;

const DashboardMetric = memo(({
  title,
  value,
  prefix,
  suffix,
  precision = 2,
  icon,
  color = "#3497D3",
  onClick,
  subtitle,
  helpText,
  howToUse,
  description,
  primary = false,
  loading = false,
  compact = false,
  /** Optional custom value formatter (e.g. signed amounts); overrides compact / count formatting. */
  formatValue,
  variant = "tile",
}) => {
  const displayFormatter = formatValue
    ? (v) => formatValue(v)
    : compact
    ? (v) => {
        if (prefix === "₦" || String(prefix).includes("₦")) {
          return formatCompactCurrency(v);
        }
        return formatCompactCount(v);
      }
    : countFormatter;

  const tooltip = helpText || howToUse
    ? (
      howToUse && helpText ? (
        <div>
          <div>{helpText}</div>
          <div className="dash-metric__help-how">{howToUse}</div>
        </div>
      ) : (helpText || howToUse)
    )
    : null;

  return (
    <div
      className={`dash-metric dash-metric--${variant} ${onClick ? "is-clickable" : ""} ${loading ? "is-loading" : ""} ${primary ? "is-primary" : ""}`}
      style={{ "--metric-accent": color }}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {loading ? (
        <MetricSkeleton variant={variant} />
      ) : (
        <>
          {icon ? (
            <div className="dash-metric__icon" style={{ color, backgroundColor: `${color}18` }}>
              {icon}
            </div>
          ) : null}
          {tooltip ? (
            <Tooltip title={tooltip}>
              <div className="dash-metric__label dash-metric__label--with-help">
                {title}
                <InfoCircleOutlined className="dash-metric__label-icon" aria-hidden />
              </div>
            </Tooltip>
          ) : (
            <div className="dash-metric__label">{title}</div>
          )}
          <div className="dash-metric__value">
            <Statistic
              prefix={compact ? null : prefix}
              suffix={compact ? null : suffix}
              value={value}
              precision={precision}
              formatter={displayFormatter}
            />
          </div>
          {subtitle ? <div className="dash-metric__hint">{subtitle}</div> : null}
        </>
      )}
    </div>
  );
});

DashboardMetric.displayName = "DashboardMetric";

export default DashboardMetric;
